
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { 
  ArrowRight, 
  Clock, 
  LineChart, 
  Sparkles, 
  CheckCircle2, 
  BarChart3,
  Zap,
  Target,
  Users,
  Settings,
  Layers
} from "lucide-react";

const ProAds = () => {
  const navigate = useNavigate();
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  
  useEffect(() => {
    const minutesInterval = setInterval(() => {
      setMinutes(prev => (prev < 5 ? prev + 1 : prev));
    }, 300);
    
    const secondsInterval = setInterval(() => {
      setSeconds(prev => (prev < 30 ? prev + 1 : prev));
    }, 100);
    
    return () => {
      clearInterval(minutesInterval);
      clearInterval(secondsInterval);
    };
  }, []);

  const features = [
    {
      icon: <Target className="h-5 w-5" />,
      title: "Advanced keyword targeting",
      description: "Fine-tune your keyword strategy with negative keyword lists, match type optimization, and SKAG structures"
    },
    {
      icon: <Zap className="h-5 w-5" />,
      title: "Performance optimization",
      description: "Leverage AI to continuously improve Quality Score, CTR, and conversion rates"
    },
    {
      icon: <Settings className="h-5 w-5" />,
      title: "Campaign automation",
      description: "Set up smart bidding strategies and automated rules to maximize ROAS"
    },
    {
      icon: <Layers className="h-5 w-5" />,
      title: "Advanced account structures",
      description: "Create complex account hierarchies with campaign groups and sophisticated targeting"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Pro Google Ads Campaigns | Advanced Features | Upfixe</title>
        <meta name="description" content="Pro-level Google Ads campaigns with advanced features, detailed analytics, and optimization strategies for growing businesses." />
        <meta name="keywords" content="pro Google Ads, advanced campaigns, detailed analytics, optimization" />
        <meta property="og:title" content="Pro Google Ads Campaigns | Advanced Features | Upfixe" />
        <meta property="og:description" content="Pro-level Google Ads campaigns with advanced features, detailed analytics, and optimization strategies for growing businesses." />
        <link rel="canonical" href="https://upfixe.com/pro-ads" />
      </Helmet>
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-24 px-4 bg-gradient-to-br from-indigo-50 to-indigo-100 border-b border-indigo-200">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <Badge className="px-3 py-1 text-sm bg-indigo-600 text-white border-indigo-700 mb-2">
                  FOR EXPERIENCED MARKETERS
                </Badge>
                
                <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                  Scale your Google Ads strategy in <span className="text-indigo-600">5 minutes</span> not hours
                </h1>
                
                <p className="text-xl text-muted-foreground">
                  For seasoned marketers who need to accelerate campaign creation, approval workflows, and performance optimization.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button size="lg" className="text-lg px-8 h-14 bg-indigo-600 hover:bg-indigo-700 font-bold" onClick={() => navigate("/auth")}>
                    Start Free Account
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 h-14 border-indigo-600 text-indigo-700 hover:bg-indigo-50 font-bold" onClick={() => navigate("/pricing")}>
                    See Pricing
                  </Button>
                </div>
                
                <p className="text-muted-foreground text-sm">
                  No credit card required. Free Plan.
                </p>
              </div>
              
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src="/public/placeholder.svg" 
                  alt="Upfixe Pro Dashboard" 
                  className="w-full h-auto"
                />
                <div className="bg-white p-6 border-t border-indigo-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-lg">Enterprise Campaign Creation</h3>
                      <p className="text-muted-foreground text-sm">Launching in {minutes} min {seconds} sec</p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 border-0">
                      Pro Feature
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm">Advanced match type strategy</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm">Multi-level targeting options</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm">Direct Google Ads posting</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Value Proposition */}
        <section className="py-20 px-4 bg-white">
          <div className="container mx-auto max-w-5xl text-center mb-16">
            <Badge className="px-3 py-1 text-sm bg-indigo-600 text-white border-indigo-700 mb-4">
              MADE FOR AGENCY SCALING
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              What used to take your team days, now takes <span className="text-indigo-600">5 minutes</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              No more bottlenecks in campaign creation. Scale your agency operations without scaling your team.
            </p>
          </div>
          
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24">
              <div className="space-y-10">
                <div className="flex gap-4">
                  <div className="bg-indigo-100 rounded-full p-3 h-12 w-12 flex items-center justify-center shrink-0">
                    <Clock className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Rapid Campaign Creation</h3>
                    <p className="text-muted-foreground">
                      Create complex campaigns with advanced targeting in minutes, not hours or days. Our AI adapts to your specifications.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-indigo-100 rounded-full p-3 h-12 w-12 flex items-center justify-center shrink-0">
                    <Sparkles className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">AI-Generated Ad Copy</h3>
                    <p className="text-muted-foreground">
                      Generate high-performance ad variations based on competitor analysis and industry benchmarks.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-indigo-100 rounded-full p-3 h-12 w-12 flex items-center justify-center shrink-0">
                    <Users className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Team Collaboration</h3>
                    <p className="text-muted-foreground">
                      Streamlined approval workflows with role-based permissions, version control, and client review options.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-10">
                <div className="flex gap-4">
                  <div className="bg-indigo-100 rounded-full p-3 h-12 w-12 flex items-center justify-center shrink-0">
                    <BarChart3 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Advanced Analytics</h3>
                    <p className="text-muted-foreground">
                      Real-time performance monitoring with actionable insights and customer journey analysis.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-indigo-100 rounded-full p-3 h-12 w-12 flex items-center justify-center shrink-0">
                    <LineChart className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">ROAS Optimization</h3>
                    <p className="text-muted-foreground">
                      AI that continuously analyzes and optimizes your campaigns for maximum return on ad spend.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="bg-indigo-100 rounded-full p-3 h-12 w-12 flex items-center justify-center shrink-0">
                    <Zap className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Direct Integration</h3>
                    <p className="text-muted-foreground">
                      Seamlessly push campaigns to Google Ads with our advanced API integration - no manual transfers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Advanced Features */}
        <section className="py-20 px-4 bg-indigo-50 border-y border-indigo-100">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <Badge className="px-3 py-1 text-sm bg-indigo-600 text-white border-indigo-700 mb-4">
                PROFESSIONAL TOOLKIT
              </Badge>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Advanced features for serious marketers
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Everything you need to create, manage, and optimize high-performance Google Ads campaigns at scale.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-all border border-indigo-200">
                  <CardContent className="p-6">
                    <div className="bg-indigo-100 rounded-full p-3 w-12 h-12 flex items-center justify-center mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* Case Study */}
        <section className="py-20 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <Badge className="px-3 py-1 text-sm bg-indigo-600 text-white border-indigo-700 mb-2">
                  CASE STUDY
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  How a 10-person agency scaled to manage 150+ Google Ads campaigns
                </h2>
                <p className="text-xl text-muted-foreground">
                  "We used to spend 3-4 hours creating each campaign. Now we can create, approve, and launch 10+ campaigns daily with the same team size."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold">
                    MJ
                  </div>
                  <div>
                    <div className="font-semibold">Michael Johnson</div>
                    <div className="text-sm text-muted-foreground">Director of Operations, AdScale Agency</div>
                  </div>
                </div>
              </div>
              
              <div className="rounded-xl overflow-hidden shadow-xl border border-indigo-100">
                <img 
                  src="/public/placeholder.svg" 
                  alt="AdScale Agency Case Study" 
                  className="w-full h-auto" 
                />
                <div className="bg-white p-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold">150+ active campaigns</h4>
                        <p className="text-sm text-muted-foreground">Managed by a team of 10</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold">87% reduction in workflow time</h4>
                        <p className="text-sm text-muted-foreground">From 3-4 hours to 15-20 minutes per campaign</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold">35% average ROAS improvement</h4>
                        <p className="text-sm text-muted-foreground">Across all client accounts</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-20 px-4 bg-indigo-600 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">
              Ready to scale your Google Ads workflow?
            </h2>
            <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto">
              Join agencies and professional marketers who have transformed their process and improved their results with Upfixe Ads.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8 h-14 bg-white text-indigo-700 hover:bg-indigo-50 font-bold" onClick={() => navigate("/auth")}>
                Start Free Account <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 h-14 border-white text-white hover:bg-indigo-700 font-bold" onClick={() => navigate("/pricing")}>
                See Pricing Plans
              </Button>
            </div>
            
            <p className="text-sm opacity-80 mt-6">
              No credit card required. Free Plan.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProAds;
